import type {PreviewData} from 'next'
import {createClient} from 'prismicio'
import type {ButtonDocument, EventDocument} from 'prismicio-types'
import {NAVIGATION_FETCH_LINKS, NAVIGATION_QUERY} from 'src/common/constants'

export const EVENTS_PER_PAGE = 6

export const EVENT_TYPES = {
  IN_PERSON: 'In person',
  ON_DEMAND: 'On demand',
  ONLINE: 'Online',
} as const

export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES]

export const formatEventTypeDisplay = (type: string): string => {
  if (type === 'In person') {
    return 'In-person'
  }

  if (type === 'On demand') {
    return 'On-demand'
  }

  return type
}

export async function fetchEventsCommonData(locale: string, previewData?: PreviewData) {
  const client = createClient({previewData})

  const [navigation, buttons, page] = await Promise.all([
    client.getSingle('navigation', {
      fetchLinks: NAVIGATION_FETCH_LINKS,
      lang: locale,
      graphQuery: NAVIGATION_QUERY,
    }),
    client.getAllByType<ButtonDocument>('button', {lang: locale}),
    client.getByUID('page', 'events-hub', {
      lang: locale,
      fetchLinks: [
        'page.parent_page',
        'event.meta_description',
        'event.title',
        'event.event_type',
        'event.event_date',
        'event.language',
        'event.location',
        'event.hero_image',
        'event.event_finish_date',
        'event.registerButton',
      ],
    }),
  ])

  return {
    navigationData: navigation.data,
    buttonsData: buttons as ButtonDocument[],
    page,
  }
}

export function groupEventsByLocale(events: EventDocument<string>[]) {
  return events.reduce(
    (acc, event) => {
      if (!acc[event.lang]) {
        acc[event.lang] = []
      }

      acc[event.lang].push(event)
      return acc
    },
    {} as Record<string, EventDocument<string>[]>,
  )
}

export function calculateTotalPages(totalEvents: number): number {
  return Math.ceil(totalEvents / EVENTS_PER_PAGE)
}

export const RELATED_EVENTS_COUNT = 3

/**
 * Selects related events from a pool based on the current event type.
 *
 * Priority order:
 * - In person: upcoming in-person → upcoming online → on-demand
 * - Online:    upcoming online → upcoming in-person → on-demand
 * - On demand: upcoming online → upcoming in-person → on-demand
 *
 * On-demand events are sorted most recent first.
 * Returns up to RELATED_EVENTS_COUNT (3) events.
 */
export function selectRelatedEvents(
  otherEvents: EventDocument[],
  currentType: string | null,
  now: Date,
): EventDocument[] {
  const nowMs = now.getTime()

  const isUpcoming = (e: EventDocument) => {
    const endDate = e.data.event_finish_date || e.data.event_date

    return endDate ? new Date(endDate).getTime() > nowMs : false
  }

  const upcomingInPerson = otherEvents.filter((e) => e.data.event_type === 'In person' && isUpcoming(e))

  const upcomingOnline = otherEvents.filter((e) => e.data.event_type === 'Online' && isUpcoming(e))

  const onDemand = otherEvents
    .filter((e) => e.data.event_type === 'On demand')
    .sort((a, b) => new Date(b.data.event_date || 0).getTime() - new Date(a.data.event_date || 0).getTime())

  const priorityMap: Record<string, EventDocument[][]> = {
    'In person': [upcomingInPerson, upcomingOnline, onDemand],
    Online: [upcomingOnline, upcomingInPerson, onDemand],
    'On demand': [upcomingOnline, upcomingInPerson, onDemand],
  }

  const pools = priorityMap[currentType ?? ''] ?? [upcomingInPerson, upcomingOnline, onDemand]

  return pools.flat().slice(0, RELATED_EVENTS_COUNT)
}

export const EVENT_GRAPH_QUERY = `
  {
    event {
      event_type
      title
      event_date
      event_finish_date
      language
      location
      meta_description
      hero_image
      parent_page
      registerButton
    }
  }
`
